import React, { useState, useEffect, useMemo } from 'react';
import {
  Layout, Move, Save, Copy, Trash2, Check, AlertTriangle,
  Layers, Loader2, Settings, Type, RotateCcw,
  Smartphone, Tablet, Monitor, MonitorSmartphone
} from 'lucide-react';
import GroupedListboxSelect from '../../ui/GroupedListboxSelect';
import api from '../../../services/api';
import * as AssessmentBuilderAPI from '../../../services/assessmentBuilderApi';
import toast from 'react-hot-toast';
import {
  DEVICE_DIMENSIONS, QUESTION_TYPE_DISPLAY, displayQuestionType,
  FONT_FAMILY_OPTIONS, LINE_SPACING_PRESETS, LINE_SPACING_MIN, LINE_SPACING_MAX, LINE_SPACING_STEP,
  DEFAULT_GLOBAL_FORMATTING
} from './constants';
import NumericInput from './NumericInput';
import TemplateManagerModal from './TemplateManagerModal';


const TEMPLATE_VRS_RESPONSE_KEYS = [
  'vrsButtonHeight',
  'vrsButtonMinWidth',
  'vrsFontSize',
  'vrsFontWeight',
  'vrsBorderRadius',
  'vrsBorderWidth',
  'vrsSelectedColor',
  'vrsUnselectedColor',
  'vrsSelectedTextColor',
  'vrsUnselectedTextColor',
  'vrsBorderColor',
  'vrsSelectedBorderColor',
  'vrsHoverColor',
  'vrsHoverBorderColor'
];

// VAS / NRS slider rendering reads these off responseConfig. They have to be
// in the template whitelist or saving a VAS layout strips the gradient + anchors.
const TEMPLATE_VAS_RESPONSE_KEYS = [
  'colorGradient',
  'leftAnchor',
  'rightAnchor',
  'min',
  'max',
  'step',
  'showTicks',
  'showLabels',
  'showValues',
  'showCurrentValue',
  'labelPosition',
];

const TEMPLATE_RESPONSE_OPTION_KEYS = [
  'spacing',
  'buttonStyle',
  'optionBoxWidth',
  'optionTextAlign',
  'optionsAlignment',
  'btnBgColor',
  'btnTextColor',
  'btnBorderColor',
  'btnSelectedBgColor',
  'btnSelectedTextColor',
  'btnSelectedBorderColor',
  'btnBorderRadius',
  'btnBorderWidth',
  'btnFontWeight',
  'optionFontSize',
  'optionPadding',
  'radioSize',
  'continuous',
  'equalWidth',
  ...TEMPLATE_VRS_RESPONSE_KEYS,
  ...TEMPLATE_VAS_RESPONSE_KEYS,
];

export const collectTemplateResponseOptions = (responseConfig = {}) => {
  // Do not default buttonStyle to 'outline': undefined means standard radio/list rendering on the
  // canvas; defaulting to outline caused saved templates to apply as boxed buttons incorrectly.
  const responseOptions = {
    display: responseConfig.layout || 'vertical',
    spacing: responseConfig.spacing ?? 8,
  };

  TEMPLATE_RESPONSE_OPTION_KEYS.forEach((key) => {
    if (responseConfig[key] !== undefined) {
      responseOptions[key] = responseConfig[key];
    }
  });

  return responseOptions;
};

// Layout widget categories — applying a template must never silently move a question
// from one widget category to another (e.g. ordinal → text input). The canvas renderer
// keys off responseConfig.layout, so a corrupt template ({question_type: 'ordinal',
// display: 'text'}) would otherwise turn an ordinal question into a textarea on apply.
const LAYOUT_CATEGORY = {
  horizontal: 'options',
  vertical: 'options',
  dropdown: 'options',
  boolean: 'options',
  text: 'text',
  nrs: 'scale',
  vas: 'scale',
  thermometer: 'scale',
  'vrs-unipolar': 'scale',
  'vrs-bipolar': 'scale',
  segmented: 'scale',
  emoji: 'scale',
  'stepped-slider': 'scale',
};

const mapTemplateDisplayToLayout = (responseOptions = {}, existingResponseConfig = {}) => {
  const display = responseOptions.display || '';
  const exactLayouts = new Set([
    'horizontal',
    'vertical',
    'dropdown',
    'text',
    'boolean',
    'vas',
    'nrs',
    'vrs-unipolar',
    'vrs-bipolar'
  ]);

  let proposed;
  if (exactLayouts.has(display)) {
    proposed = display;
  } else if (display.includes('horizontal') && display.includes('slider')) {
    proposed = responseOptions.continuous ? 'vas' : 'nrs';
  } else if (display.includes('horizontal')) {
    proposed = 'horizontal';
  } else if (display.includes('vertical')) {
    proposed = 'vertical';
  } else if (display === 'textarea') {
    proposed = 'text';
  } else if (display === 'dropdown') {
    proposed = 'dropdown';
  } else {
    return existingResponseConfig.layout;
  }

  // Guard against bad template data: refuse to change widget category. If the
  // current layout is options-list (vertical/horizontal/dropdown) the template
  // must propose another options-list value, not a text or scale layout.
  const existingCategory = LAYOUT_CATEGORY[existingResponseConfig.layout];
  const proposedCategory = LAYOUT_CATEGORY[proposed];
  if (existingCategory && proposedCategory && existingCategory !== proposedCategory) {
    return existingResponseConfig.layout;
  }
  return proposed;
};

const buildTemplatePlacement = (config = {}, existingPosition = {}, existingSize = {}, canvasDims = {}) => {
  const canvasWidth = canvasDims.width || 1280;
  const canvasHeight = canvasDims.height || 900;
  const navButtonSpace = 100;
  const fallbackWidth = existingSize.width ?? Math.round(canvasWidth * 0.9);
  const fallbackHeight = existingSize.height ?? 220;
  const width = config.position?.width ?? fallbackWidth;
  const height = config.position?.height ?? fallbackHeight;
  const availableHeight = canvasHeight - navButtonSpace;

  return {
    position: {
      x: config.position?.x ?? existingPosition.x ?? Math.round((canvasWidth - width) / 2),
      y: config.position?.y ?? existingPosition.y ?? Math.round((availableHeight - height) / 2),
    },
    size: {
      width,
      height,
    }
  };
};

export const buildTemplateResponseConfig = (config = {}, existingResponseConfig = {}) => {
  const responseOptions = config.responseOptions || {};
  const mergedResponseConfig = {
    ...existingResponseConfig,
    layout: mapTemplateDisplayToLayout(responseOptions, existingResponseConfig),
    spacing: responseOptions.spacing ?? existingResponseConfig.spacing,
    alignment: config.alignment || existingResponseConfig.alignment,
  };

  // Only keys present on the template should override; omitted buttonStyle means radio-style options.
  if ('buttonStyle' in responseOptions) {
    mergedResponseConfig.buttonStyle = responseOptions.buttonStyle;
  } else {
    delete mergedResponseConfig.buttonStyle;
  }

  TEMPLATE_RESPONSE_OPTION_KEYS.forEach((key) => {
    if (key === 'buttonStyle') return;
    if (responseOptions[key] !== undefined) {
      mergedResponseConfig[key] = responseOptions[key];
    }
  });

  // System VAS/NRS templates store anchors as leftLabel/rightLabel; the canvas
  // VASSlider reads leftAnchor/rightAnchor. Translate so a system template applies.
  if (responseOptions.leftLabel !== undefined && responseOptions.leftAnchor === undefined) {
    mergedResponseConfig.leftAnchor = responseOptions.leftLabel;
  }
  if (responseOptions.rightLabel !== undefined && responseOptions.rightAnchor === undefined) {
    mergedResponseConfig.rightAnchor = responseOptions.rightLabel;
  }

  return mergedResponseConfig;
};

// Apply a layout template's config to a single canvas element, including ungrouped
// sub-element handling. Used by single-element and bulk-apply paths so they stay in sync.
// Without this, bulk apply only updates the parent element's style/position/responseConfig
// and leaves stale subElements on ungrouped questions, which the canvas renders instead
// (FECOA-1337).
const applyTemplateConfigToElement = (el, config, canvasDims) => {
  const placement = buildTemplatePlacement(config, el.position || {}, el.size || {}, canvasDims);
  const updated = {
    ...el,
    position: placement.position,
    size: placement.size,
    style: {
      ...el.style,
      ...(config.style || {})
    },
    responseConfig: buildTemplateResponseConfig(config, el.responseConfig || {}),
    ...(config.is_required !== undefined && { required: config.is_required }),
  };

  if (config.isUngrouped && config.subElementLayout) {
    const options = el.responseOptions || el.data?.options || [];
    const textLayout = config.subElementLayout.find(s => s.type === 'question-text');
    const optLayouts = config.subElementLayout.filter(s => s.type === 'question-option');

    updated.isUngrouped = true;
    updated.subElements = [
      {
        id: `${el.id}_text`,
        type: 'question-text',
        parentId: el.id,
        content: el.data?.text || el.content || '',
        position: textLayout?.position || { x: 0, y: 0 },
        size: textLayout?.size || { width: placement.size.width, height: 60 },
        style: textLayout?.style || { ...updated.style }
      },
      ...options.map((option, idx) => {
        const tmplOpt = optLayouts[idx] || optLayouts[optLayouts.length - 1];
        const fallbackY = (textLayout?.size?.height || 60) + 20 + idx * 40;
        return {
          id: `${el.id}_option_${idx}`,
          type: 'question-option',
          parentId: el.id,
          optionIndex: idx,
          content: typeof option === 'string' ? option : (option.label || option),
          value: typeof option === 'object' ? option.value : idx,
          position: tmplOpt?.position || { x: 0, y: fallbackY },
          size: tmplOpt?.size || { width: placement.size.width, height: 32 },
          style: tmplOpt?.style || { fontSize: updated.responseConfig?.optionFontSize || 14 }
        };
      })
    ];
  } else if (el.isUngrouped) {
    updated.isUngrouped = false;
    updated.subElements = undefined;
  }

  return updated;
};

// Mirrors backend COMPATIBLE_QUESTION_TYPE_GROUPS. Templates apply across types in the
// same group — without this, a sponsor "nominal" template silently 400s on questions
// imported as 'multiple_choice' (FECOA-1337). Numeric/vas/nrs are NOT grouped: they are
// distinct widget types, not naming aliases. `getEffectiveQuestionType` resolves the
// legacy `type='numeric'+layout='vas'` shape to 'vas' upstream of this check.
const COMPATIBLE_QUESTION_TYPE_GROUPS = [
  ['nominal', 'multiple_choice', 'checkbox', 'dropdown'],
  ['yes_no', 'boolean'],
];

export const questionTypeCompatible = (questionType, templateType) => {
  if (!questionType || !templateType) return false;
  if (questionType === templateType) return true;
  return COMPATIBLE_QUESTION_TYPE_GROUPS.some(
    group => group.includes(questionType) && group.includes(templateType)
  );
};

// Items from CDISC import / item bank don't all carry the same shape — some have
// `type`, some `response_type`, some `responseType`, some `question_type`. Resolve
// in priority order so downstream gating and matching aren't fooled by missing fields.
export const getElementQuestionType = (element) => {
  if (!element) return undefined;
  const data = element.data || {};
  return (
    data.type ||
    data.response_type ||
    data.responseType ||
    data.question_type ||
    element.questionType ||
    undefined
  );
};

// Numeric-type elements render as NRS/VAS based on responseConfig.layout. Layout templates
// are seeded under the visual type (nrs/vas), so we key lookups by layout when it's a
// first-class question type, falling back to the resolved data type otherwise. Instructions
// have type='instruction' on the element itself rather than under data.
export const getEffectiveQuestionType = (element) => {
  if (!element) return 'ordinal';
  if (element.type === 'instruction') return 'instruction';
  const dataType = getElementQuestionType(element);
  const layout = element.responseConfig?.layout;
  if (dataType === 'numeric' && (layout === 'nrs' || layout === 'vas')) return layout;
  return dataType || 'ordinal';
};

const getTemplateDeviceCategory = (template) => template?.device_category || 'all';

const matchesTemplateDevice = (template, currentDeviceCategory) => {
  const templateDeviceCategory = getTemplateDeviceCategory(template);
  return templateDeviceCategory === currentDeviceCategory || templateDeviceCategory === 'all';
};

const SaveQuestionTemplateSection = ({ selectedElementData, assessmentId, deviceMode, onTemplateSaved }) => {
  const [name, setName] = useState('');
  const [level, setLevel] = useState('assessment');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!name.trim()) return;

    const layoutConfig = {
      position: {
        x: selectedElementData.position?.x || 0,
        y: selectedElementData.position?.y || 0,
        width: selectedElementData.size?.width || 600,
        height: selectedElementData.size?.height || 200,
      },
      responseOptions: collectTemplateResponseOptions(selectedElementData.responseConfig || {}),
      style: selectedElementData.style || {},
      alignment: selectedElementData.responseConfig?.alignment || 'left',
      is_required: selectedElementData.required || false,
    };

    // Save free-position layout if ungrouped
    if (selectedElementData.isUngrouped && selectedElementData.subElements) {
      layoutConfig.isUngrouped = true;
      // Store sub-element positions relative to the element, normalized by option index
      layoutConfig.subElementLayout = selectedElementData.subElements.map(sub => ({
        type: sub.type,
        optionIndex: sub.optionIndex,
        position: { ...sub.position },
        size: { ...sub.size },
        style: sub.style ? { ...sub.style } : undefined
      }));
    }

    setSaving(true);
    try {
      await AssessmentBuilderAPI.saveQuestionLayoutTemplate({
        name: name.trim(),
        question_type: getEffectiveQuestionType(selectedElementData),
        layout_config: layoutConfig,
        level: level,
        // The responsive canvas is device-agnostic; its layout templates apply
        // to all device categories. The backend only accepts all/phone/tablet/
        // desktop, so map 'responsive' → 'all' (FECOA-1532).
        device_category: (DEVICE_DIMENSIONS[deviceMode]?.responsive ? 'all' : DEVICE_DIMENSIONS[deviceMode]?.category) || 'all',
        device_mode: deviceMode || '',
        ...(level === 'assessment' && { assessment_id: assessmentId }),
      });
      toast.success(`Saved "${name.trim()}"`);
      setName('');
      if (onTemplateSaved) onTemplateSaved();
    } catch (error) {
      console.error('Failed to save template:', error);
      toast.error(error.message || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-gradient-to-br from-white to-green-50/30 dark:from-gray-800 dark:to-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700 shadow-sm dark:shadow-gray-900/20">
      <div className="flex items-center space-x-2 mb-3">
        <div className="p-1.5 bg-green-100 text-green-600 rounded-lg">
          <Save className="w-3.5 h-3.5" />
        </div>
        <div>
          <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Save as Template</h4>
          <p className="text-[9px] text-gray-400 mt-0.5">Capture this question's layout for reuse</p>
        </div>
      </div>
      <p className="text-[10px] text-primary-700 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20 rounded-lg px-2.5 py-1.5 mb-2 leading-relaxed">
        This template will include the current question's layout, styling, and metadata such as requirement status{selectedElementData?.required ? <span className="font-medium"> (currently required)</span> : ''}. To change what's included, update the question settings before saving.
      </p>
      <div className="space-y-2">
        <input
          type="text"
          placeholder='Template name (e.g., "Centered Ordinal")'
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full px-3 py-2 text-xs bg-white border-2 border-gray-200 rounded-lg focus:border-green-400 focus:ring-2 focus:ring-green-100 transition-all"
        />
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => setLevel('assessment')}
            className={`px-3 py-1.5 text-xs rounded-lg border-2 transition-all ${
              level === 'assessment'
                ? 'border-green-500 bg-green-50 text-green-700 font-medium'
                : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
            }`}
          >
            This Assessment
          </button>
          <button
            type="button"
            onClick={() => setLevel('sponsor')}
            className={`px-3 py-1.5 text-xs rounded-lg border-2 transition-all ${
              level === 'sponsor'
                ? 'border-green-500 bg-green-50 text-green-700 font-medium'
                : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
            }`}
          >
            Sponsor Library
          </button>
        </div>
        <button
          onClick={handleSave}
          disabled={!name.trim() || saving}
          className="w-full px-3 py-2 text-xs font-medium bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
        >
          {saving ? 'Saving...' : 'Save Template'}
        </button>
      </div>
    </div>
  );
};

// Form-wide formatting controls (FECOA-732). Sets the font family, base font size,
// and line spacing for the form. Saving cascades these onto every unlocked
// question/instruction already on the canvas and seeds newly added ones; locked
// elements keep their own styling. "Reset to Publisher Defaults" reverts the form
// values to the system template values (and re-cascades to unlocked elements).
const FormFormattingSection = ({ globalFormatting, onGlobalFormattingChange }) => {
  const current = useMemo(
    () => ({ ...DEFAULT_GLOBAL_FORMATTING, ...(globalFormatting || {}) }),
    [globalFormatting]
  );
  const [draft, setDraft] = useState(current);
  const [saving, setSaving] = useState(false);

  // Re-sync the draft when the persisted value changes (e.g. after a reset),
  // unless the user is mid-edit with unsaved changes.
  const isDirty =
    draft.fontFamily !== current.fontFamily ||
    Number(draft.baseFontSize) !== Number(current.baseFontSize) ||
    Number(draft.lineSpacing) !== Number(current.lineSpacing);

  useEffect(() => {
    setDraft(current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current.fontFamily, current.baseFontSize, current.lineSpacing]);

  const handleSave = async () => {
    if (!isDirty || saving) return;
    setSaving(true);
    try {
      await onGlobalFormattingChange({
        fontFamily: draft.fontFamily,
        baseFontSize: Number(draft.baseFontSize),
        lineSpacing: Number(draft.lineSpacing),
      });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (saving) return;
    setSaving(true);
    try {
      await onGlobalFormattingChange({ reset: true });
    } finally {
      setSaving(false);
    }
  };

  const spacingValue = Number(draft.lineSpacing);

  return (
    <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 flex-shrink-0">
      <div className="flex items-center gap-2 mb-3">
        <div className="p-1.5 bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 rounded-lg">
          <Type className="w-4 h-4" />
        </div>
        <div>
          <h3 className="text-xs font-bold text-gray-800 uppercase tracking-wide">Form Formatting</h3>
          <p className="text-[10px] text-gray-500">Default font &amp; spacing for new questions on this form</p>
        </div>
      </div>

      <div className="space-y-3">
        {/* Font family */}
        <div>
          <label className="block text-[10px] font-medium text-gray-700 mb-1 uppercase tracking-wide">Font Family</label>
          <select
            value={draft.fontFamily}
            onChange={(e) => setDraft(d => ({ ...d, fontFamily: e.target.value }))}
            style={{ fontFamily: draft.fontFamily }}
            className="w-full px-3 py-2 text-xs bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-lg focus:border-amber-400 focus:ring-2 focus:ring-amber-100 transition-all"
          >
            {FONT_FAMILY_OPTIONS.map((option) => (
              <option key={option.value} value={option.value} style={{ fontFamily: option.value }}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Base font size */}
        <div>
          <label className="block text-[10px] font-medium text-gray-700 mb-1 uppercase tracking-wide">Base Font Size (px)</label>
          <NumericInput
            value={draft.baseFontSize}
            onChange={(val) => setDraft(d => ({ ...d, baseFontSize: val }))}
            min={8}
            max={72}
            fallback={DEFAULT_GLOBAL_FORMATTING.baseFontSize}
            className="w-full px-3 py-2 text-xs bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-lg focus:border-amber-400 focus:ring-2 focus:ring-amber-100 transition-all"
          />
        </div>

        {/* Line spacing presets + numeric */}
        <div>
          <label className="block text-[10px] font-medium text-gray-700 mb-1 uppercase tracking-wide">Line Spacing</label>
          <div className="grid grid-cols-4 gap-1.5 mb-2">
            {LINE_SPACING_PRESETS.map((preset) => (
              <button
                key={preset.value}
                type="button"
                onClick={() => setDraft(d => ({ ...d, lineSpacing: preset.value }))}
                className={`px-2 py-1.5 text-[11px] rounded-lg border-2 transition-all ${
                  Math.abs(spacingValue - preset.value) < 0.001
                    ? 'border-amber-500 bg-amber-50 text-amber-700 font-medium'
                    : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-600 hover:border-gray-300'
                }`}
              >
                {preset.label}
              </button>
            ))}
          </div>
          <NumericInput
            value={draft.lineSpacing}
            onChange={(val) => setDraft(d => ({ ...d, lineSpacing: val }))}
            min={LINE_SPACING_MIN}
            max={LINE_SPACING_MAX}
            step={LINE_SPACING_STEP}
            float
            fallback={DEFAULT_GLOBAL_FORMATTING.lineSpacing}
            className="w-full px-3 py-2 text-xs bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-lg focus:border-amber-400 focus:ring-2 focus:ring-amber-100 transition-all"
          />
          <p className="text-[9px] text-gray-400 mt-1">Between {LINE_SPACING_MIN} and {LINE_SPACING_MAX}.</p>
        </div>

        <button
          onClick={handleSave}
          disabled={!isDirty || saving}
          className="w-full px-3 py-2 text-xs font-medium bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
        >
          {saving ? 'Saving…' : 'Save Form Formatting'}
        </button>

        <button
          onClick={handleReset}
          disabled={saving}
          className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 text-[11px] text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 transition-all"
        >
          <RotateCcw className="w-3.5 h-3.5" />
          Reset to Publisher Defaults
        </button>

        <p className="text-[10px] text-gray-500 bg-amber-50 dark:bg-amber-900/10 rounded-lg px-2.5 py-1.5 leading-relaxed">
          Restyles <strong>all unlocked</strong> questions and instructions on this form and sets the default for new ones. Lock an element to keep its own styling.
        </p>
      </div>
    </div>
  );
};

const LayoutPanel = ({
  selectedElementData,
  selectedElement,
  screens,
  currentScreenIndex,
  setScreens,
  buttonTemplates,
  selectedButtonTemplate,
  handleLoadButtonTemplate,
  handleSaveButtonTemplate,
  handleDeleteButtonTemplate,
  handleApplyTemplateToAllScreens,
  newTemplateName,
  setNewTemplateName,
  templateSaveLevel,
  setTemplateSaveLevel,
  setTemplateAsDefault,
  setSetTemplateAsDefault,
  calculateQuestionHeight,
  distributeOptions,
  alignOptions,
  assessmentId,
  deviceMode,
  deviceDimensions,
  handleDeviceModeChange,
  allTemplatesCache,
  refreshTemplatesCache,
  appliedDefaultTemplateIds,
  onDefaultTemplateSet,
  globalFormatting,
  onGlobalFormattingChange
}) => {
  const displayType = displayQuestionType;

  // Safety checks for props
  const safeScreens = screens || [];
  const templates = buttonTemplates || [];

  // Get current screen
  const currentScreen = safeScreens[currentScreenIndex];

  // Question Layout Template state
  const [questionTemplates, setQuestionTemplates] = useState({ system: [], sponsor: [], user: [], assessment: [], other: [] });
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [selectedQuestionTemplate, setSelectedQuestionTemplate] = useState('');
  const [showTemplateManager, setShowTemplateManager] = useState(false);

  // Delete confirmation state (shared for button + question templates)
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { id, name, type: 'question' | 'button' }
  const [deleteTypedName, setDeleteTypedName] = useState('');

  // Get current device category from deviceMode
  const currentDeviceCategory = DEVICE_DIMENSIONS[deviceMode]?.category || 'desktop';

  // Responsive (fluid) canvas is controlled by a dedicated toggle rather than the
  // device-size dropdown — when on it "takes over" and the canvas fills the full
  // width of any screen (FECOA-1532). It is always available on every assessment.
  const isResponsiveCanvas = deviceMode === 'responsive';

  // Remember the last fixed device so toggling responsive off restores it.
  const [lastFixedDeviceMode, setLastFixedDeviceMode] = useState(
    deviceMode && deviceMode !== 'responsive' ? deviceMode : 'desktop-standard'
  );
  useEffect(() => {
    if (deviceMode && deviceMode !== 'responsive') {
      setLastFixedDeviceMode(deviceMode);
    }
  }, [deviceMode]);

  const handleResponsiveToggle = (enabled) => {
    if (typeof handleDeviceModeChange !== 'function') return;
    handleDeviceModeChange(enabled ? 'responsive' : (lastFixedDeviceMode || 'desktop-standard'));
  };

  /** Grouped options for the fixed device-size picker (responsive is the toggle above). */
  const devicePresetGroups = useMemo(() => {
    const dims = deviceDimensions || {};
    const entries = Object.entries(dims);
    const toOpts = (list) =>
      list.map(([key, d]) => ({
        value: key,
        label: d.name,
        description: `${d.width} × ${d.height}px`,
      }));
    const groups = [];
    const phones = entries.filter(([, d]) => d.category === 'phone');
    const tablets = entries.filter(([, d]) => d.category === 'tablet');
    const desktops = entries.filter(([, d]) => d.category === 'desktop');
    if (phones.length) {
      groups.push({ id: 'phone', label: 'Mobile', icon: Smartphone, options: toOpts(phones) });
    }
    if (tablets.length) {
      groups.push({ id: 'tablet', label: 'Tablets', icon: Tablet, options: toOpts(tablets) });
    }
    if (desktops.length) {
      groups.push({ id: 'desktop', label: 'Desktop', icon: Monitor, options: toOpts(desktops) });
    }
    return groups;
  }, [deviceDimensions]);

  // Get the currently selected question template object
  const getSelectedQuestionTemplateObj = () => {
    if (!selectedQuestionTemplate) return null;
    const all = [
      ...questionTemplates.system,
      ...questionTemplates.sponsor,
      ...questionTemplates.user,
      ...questionTemplates.assessment,
      ...questionTemplates.other.map(o => o.template)
    ];
    return all.find(t => String(t.id) === selectedQuestionTemplate) || null;
  };

  const selectedQTemplateObj = getSelectedQuestionTemplateObj();

  // Check if selected question template is deletable (user-created or assessment-level)
  const isSelectedQTemplateDeletable = selectedQTemplateObj && (
    questionTemplates.user.some(t => String(t.id) === selectedQuestionTemplate) ||
    questionTemplates.assessment.some(t => String(t.id) === selectedQuestionTemplate)
  );

  // Layout templates can target question elements OR instruction elements (FECOA-1335).
  // Other element types (button, divider) are not templatable.
  const isTemplatableElement = (el) => el?.type === 'question' || el?.type === 'instruction';

  // Count templatable elements compatible with the selected template across all screens
  // (excluding the current element). Compat-based — see questionTypeCompatible —
  // so a 'nominal' template counts both 'nominal' and 'multiple_choice' questions.
  const countSimilarElements = () => {
    if (!selectedQTemplateObj || !selectedElementData) return 0;
    const templateType = selectedQTemplateObj.question_type || getEffectiveQuestionType(selectedElementData);
    if (!templateType) return 0;
    let count = 0;
    safeScreens.forEach(screen => {
      screen.elements?.forEach(el => {
        if (!isTemplatableElement(el) || el.id === selectedElement) return;
        const elType = getEffectiveQuestionType(el);
        if (questionTypeCompatible(elType, templateType)) count++;
      });
    });
    return count;
  };

  const similarCount = selectedQTemplateObj ? countSimilarElements() : 0;

  // Apply selected template to ALL compatible question elements across all screens + set as default
  const applyTemplateToSimilarElements = async () => {
    if (!selectedQTemplateObj || !selectedElementData) return;
    const templateType = selectedQTemplateObj.question_type || getEffectiveQuestionType(selectedElementData);
    if (!templateType) return;
    const displayedType = displayType(getEffectiveQuestionType(selectedElementData));

    const config = { ...selectedQTemplateObj.layout_config };
    delete config.navigation;

    const canvasDims = DEVICE_DIMENSIONS[deviceMode] || DEVICE_DIMENSIONS['desktop-standard'] || { width: 1280, height: 900 };
    // Collect screen_question_ids from matching elements for backend bulk-apply
    const screenQuestionIds = [];
    const matchedElementsDebug = [];
    let lockedSkipCount = 0;
    let appliedCount = 0;
    let unsavedSkipCount = 0;
    safeScreens.forEach(screen => {
      screen.elements?.forEach(el => {
        if (!isTemplatableElement(el)) return;
        const elType = getEffectiveQuestionType(el);
        if (!questionTypeCompatible(elType, templateType)) return;
        if (el.isLocked) {
          lockedSkipCount += 1;
          return;
        }
        appliedCount += 1;
        matchedElementsDebug.push({
          id: el.id,
          elType,
          screen_question_id: el.screen_question_id,
          isUngrouped: !!el.isUngrouped,
          subElementCount: el.subElements?.length || 0,
        });
        if (el.screen_question_id) {
          screenQuestionIds.push(el.screen_question_id);
        } else if (el.type === 'question') {
          // Only unsaved question elements block server persistence. Instruction
          // elements never get a screen_question_id (save_screens only creates
          // ScreenQuestion rows for question elements); their applied styles
          // persist through screen layout_data instead.
          unsavedSkipCount += 1;
        }
      });
    });
    console.log('[FECOA-1337] applyTemplateToSimilarElements', {
      templateType,
      templateId: selectedQTemplateObj.id,
      templateName: selectedQTemplateObj.name,
      configHasIsUngrouped: !!config.isUngrouped,
      configHasSubElementLayout: !!config.subElementLayout,
      matchedElements: matchedElementsDebug,
      screenQuestionIds,
      lockedSkipCount,
      unsavedSkipCount,
    });

    // Client-side update for immediate UI feedback
    const updatedScreens = safeScreens.map(screen => ({
      ...screen,
      elements: screen.elements.map(el => {
        if (!isTemplatableElement(el) || el.isLocked) return el;
        const elType = getEffectiveQuestionType(el);
        if (!questionTypeCompatible(elType, templateType)) return el;
        return applyTemplateConfigToElement(el, config, canvasDims);
      })
    }));
    setScreens(updatedScreens);

    // Backend bulk-apply for server-side persistence + audit logging
    let bulkApplyError = null;
    if (screenQuestionIds.length > 0) {
      try {
        const bulkResult = await api.post(
          `/api/assessment-builder/layout-templates/${selectedQTemplateObj.id}/bulk-apply/`,
          { screen_question_ids: screenQuestionIds }
        );
        console.log('[BULK_APPLY] Backend result:', bulkResult.data);
        const data = bulkResult.data || {};
        if (data.success === false) {
          bulkApplyError = (data.errors && data.errors[0]) || 'Backend bulk-apply failed';
        } else if (Array.isArray(data.errors) && data.errors.length > 0) {
          // Partial success — backend updated some questions but rejected others (e.g. type mismatch).
          // Without surfacing this, the toast says "Applied to N" while only some questions changed (FECOA-1337).
          bulkApplyError = `Applied to ${data.success_count} of ${screenQuestionIds.length} — ${data.errors[0]}`;
        } else if (typeof data.success_count === 'number' && data.success_count < screenQuestionIds.length) {
          bulkApplyError = `Applied to ${data.success_count} of ${screenQuestionIds.length} questions on the server`;
        }
      } catch (err) {
        console.warn('[BULK_APPLY] Backend bulk-apply failed (client-side update still applied):', err);
        bulkApplyError = err?.response?.data?.errors?.[0] || err?.response?.data?.error || err?.message || 'Backend bulk-apply failed';
      }
    }
    // Matching question elements without screen_question_id were just-added and not yet
    // persisted. Their canvas appearance updates immediately, but the bulk-apply skips
    // them, so the change disappears on next save/reload. Surface so the user knows to
    // save first (FECOA-1337).
    if (!bulkApplyError && unsavedSkipCount > 0) {
      bulkApplyError = `${unsavedSkipCount} matching question${unsavedSkipCount > 1 ? 's are' : ' is'} not yet saved — save the assessment first to persist the template across them.`;
    }

    if (bulkApplyError) {
      toast.error(`Template not saved on server: ${bulkApplyError}`);
      return;
    }

    // Also set as default for this question type
    try {
      await AssessmentBuilderAPI.setLayoutDefault({
        template_id: selectedQTemplateObj.id,
        assessment_id: assessmentId,
        question_type: templateType
      });
      if (onDefaultTemplateSet) {
        onDefaultTemplateSet(selectedQTemplateObj, templateType);
      }
      const skipSuffix = lockedSkipCount > 0 ? `, ${lockedSkipCount} skipped (locked)` : '';
      toast.success(`Applied to ${appliedCount} ${displayedType} elements${skipSuffix} and set as default`);
    } catch (err) {
      console.error('Failed to set default:', err);
      const skipSuffix = lockedSkipCount > 0 ? `, ${lockedSkipCount} skipped (locked)` : '';
      toast.success(`Applied to ${appliedCount} ${displayedType} elements${skipSuffix} (default not set)`);
    }
  };

  // Wrapper for TemplateManagerModal's "Use for Assessment" button.
  // Accepts a template object and applies it to all compatible question elements.
  const applyTemplateToAllOfType = async (template) => {
    const templateType = template.question_type || 'ordinal';
    const config = { ...template.layout_config };
    delete config.navigation;

    const canvasDims = DEVICE_DIMENSIONS[deviceMode] || DEVICE_DIMENSIONS['desktop-standard'] || { width: 1280, height: 900 };
    const screenQuestionIds = [];
    let lockedSkipCount = 0;
    let appliedCount = 0;
    let unsavedSkipCount = 0;
    safeScreens.forEach(screen => {
      screen.elements?.forEach(el => {
        if (!isTemplatableElement(el)) return;
        const elType = getEffectiveQuestionType(el);
        if (!questionTypeCompatible(elType, templateType)) return;
        if (el.isLocked) {
          lockedSkipCount += 1;
          return;
        }
        appliedCount += 1;
        if (el.screen_question_id) {
          screenQuestionIds.push(el.screen_question_id);
        } else if (el.type === 'question') {
          // Instruction elements never get a screen_question_id; see
          // applyTemplateToSimilarElements for why they don't count as unsaved.
          unsavedSkipCount += 1;
        }
      });
    });

    const updatedScreens = safeScreens.map(screen => ({
      ...screen,
      elements: screen.elements.map(el => {
        if (!isTemplatableElement(el) || el.isLocked) return el;
        const elType = getEffectiveQuestionType(el);
        if (!questionTypeCompatible(elType, templateType)) return el;
        return applyTemplateConfigToElement(el, config, canvasDims);
      })
    }));
    setScreens(updatedScreens);

    let bulkApplyError = null;
    if (screenQuestionIds.length > 0) {
      try {
        const bulkResult = await api.post(
          `/api/assessment-builder/layout-templates/${template.id}/bulk-apply/`,
          { screen_question_ids: screenQuestionIds }
        );
        const data = bulkResult.data || {};
        if (data.success === false) {
          bulkApplyError = (data.errors && data.errors[0]) || 'Backend bulk-apply failed';
        } else if (Array.isArray(data.errors) && data.errors.length > 0) {
          bulkApplyError = `Applied to ${data.success_count} of ${screenQuestionIds.length} — ${data.errors[0]}`;
        } else if (typeof data.success_count === 'number' && data.success_count < screenQuestionIds.length) {
          bulkApplyError = `Applied to ${data.success_count} of ${screenQuestionIds.length} questions on the server`;
        }
      } catch (err) {
        console.warn('[BULK_APPLY] Backend bulk-apply failed (client-side update still applied):', err);
        bulkApplyError = err?.response?.data?.errors?.[0] || err?.response?.data?.error || err?.message || 'Backend bulk-apply failed';
      }
    }
    if (!bulkApplyError && unsavedSkipCount > 0) {
      bulkApplyError = `${unsavedSkipCount} matching question${unsavedSkipCount > 1 ? 's are' : ' is'} not yet saved — save the assessment first to persist the template across them.`;
    }

    if (bulkApplyError) {
      toast.error(`Template not saved on server: ${bulkApplyError}`);
      return;
    }

    await AssessmentBuilderAPI.setLayoutDefault({
      template_id: template.id,
      assessment_id: assessmentId,
      question_type: templateType
    });
    if (onDefaultTemplateSet) {
      onDefaultTemplateSet(template, templateType);
    }
    const skipSuffix = lockedSkipCount > 0 ? `, ${lockedSkipCount} skipped (locked)` : '';
    toast.success(`Applied to ${appliedCount} ${displayType(templateType)} elements${skipSuffix}`);
  };

  // Delete a question layout template
  const handleDeleteQuestionTemplate = async () => {
    if (!deleteConfirm || deleteConfirm.type !== 'question') return;
    try {
      await api.delete(`/api/assessment-builder/layout-templates/${deleteConfirm.id}/`);
      toast.success('Template deleted');
      setDeleteConfirm(null);
      setDeleteTypedName('');
      setSelectedQuestionTemplate('');
      // Re-fetch templates
      const questionType = getEffectiveQuestionType(selectedElementData);
      const response = await api.get('/api/assessment-builder/layout-templates/available/', {
        params: { question_type: questionType, assessment_id: assessmentId }
      });
      setQuestionTemplates({
        system: response.data.system_templates || [],
        sponsor: response.data.sponsor_templates || [],
        user: response.data.user_templates || [],
        assessment: response.data.assessment_templates || [],
        other: response.data.assessment_templates_from_other_assessments || []
      });
      // The Manage Assessment Templates modal reads the mount-time preload cache,
      // not this panel's list — invalidate it or the deleted template lingers
      // there until a full page reload (FECOA-1844).
      if (refreshTemplatesCache) refreshTemplatesCache();
    } catch (error) {
      console.error('Failed to delete template:', error);
      toast.error('Failed to delete template');
    }
  };

  // Delete a button layout template
  const handleDeleteButtonTemplateConfirmed = async () => {
    if (!deleteConfirm || deleteConfirm.type !== 'button') return;
    try {
      await handleDeleteButtonTemplate(deleteConfirm.id);
      setDeleteConfirm(null);
      setDeleteTypedName('');
    } catch (error) {
      console.error('Failed to delete button template:', error);
    }
  };

  // Check if selected button template is deletable (user or assessment level)
  const selectedBtnTemplateObj = templates.find(t => String(t.id) === String(selectedButtonTemplate));
  const isSelectedBtnTemplateDeletable = selectedBtnTemplateObj &&
    (selectedBtnTemplateObj.level === 'user' || selectedBtnTemplateObj.level === 'assessment');

  // Fetch layout templates when a templatable element (question or instruction)
  // is selected. Gating on type === 'question' left the dropdown empty for
  // instruction elements until an unrelated save repopulated it (FECOA-1335).
  useEffect(() => {
    const fetchQuestionTemplates = async () => {
      if (!isTemplatableElement(selectedElementData)) return;
      const questionType = getEffectiveQuestionType(selectedElementData);
      setLoadingTemplates(true);
      try {
        const response = await api.get('/api/assessment-builder/layout-templates/available/', {
          params: {
            question_type: questionType,
            assessment_id: assessmentId
          }
        });
        setQuestionTemplates({
          system: response.data.system_templates || [],
          sponsor: response.data.sponsor_templates || [],
          user: response.data.user_templates || [],
          assessment: response.data.assessment_templates || [],
          other: response.data.assessment_templates_from_other_assessments || []
        });
      } catch (error) {
        console.error('Failed to fetch question templates:', error);
      } finally {
        setLoadingTemplates(false);
      }
    };

    fetchQuestionTemplates();
  }, [selectedElementData?.id, selectedElementData?.data?.type, selectedElementData?.responseConfig?.layout, assessmentId]);
  
  // Apply a question layout template
  const applyQuestionTemplate = (template) => {
    if (!template?.layout_config || !selectedElement) return;

    const { navigation: _nav, ...config } = template.layout_config;
    const canvasDims = DEVICE_DIMENSIONS[deviceMode] || DEVICE_DIMENSIONS['desktop-standard'] || { width: 1280, height: 900 };

    const updatedScreens = safeScreens.map((screen, idx) => {
      if (idx !== currentScreenIndex) return screen;
      return {
        ...screen,
        elements: screen.elements.map(el =>
          el.id === selectedElement ? applyTemplateConfigToElement(el, config, canvasDims) : el
        )
      };
    });
    setScreens(updatedScreens);
  };

  // Scale layouts render from responseConfig (min/max/anchors), not from a responseOptions array —
  // allow template apply/save for them even when responseOptions is empty.
  const CONFIG_DRIVEN_SCALE_LAYOUTS = ['nrs', 'vas', 'thermometer', 'vrs-unipolar', 'vrs-bipolar', 'segmented', 'stepped-slider', 'emoji'];
  // Text-input question types have no responseOptions but still have a layout (position, size, textarea config) worth templating.
  const TEXT_INPUT_QUESTION_TYPES = ['text', 'free_text'];
  const resolvedElementType = getElementQuestionType(selectedElementData);
  const hasTemplatableResponseUI =
    // Instructions are placed on the canvas as type='instruction' (not 'question'), but they
    // still have layout (position, font, color, padding) worth templating (FECOA-1335).
    selectedElementData?.type === 'instruction' ||
    (selectedElementData?.type === 'question' && (
      selectedElementData.responseOptions?.length > 0 ||
      (selectedElementData.isUngrouped && selectedElementData.subElements?.length > 0) ||
      CONFIG_DRIVEN_SCALE_LAYOUTS.includes(selectedElementData.responseConfig?.layout) ||
      TEXT_INPUT_QUESTION_TYPES.includes(resolvedElementType)
    ));

  // UNIFIED LAYOUT: always show screen-level sections, conditionally show element section below
  return (
    <div className="flex flex-col h-full overflow-y-auto">

      {/* Canvas size — responsive toggle (takes over) + fixed device presets */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 flex-shrink-0">
        {/* Responsive toggle — always available; overrides the device size below */}
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 text-xs font-semibold tracking-wide text-gray-800">
              <MonitorSmartphone className="h-3.5 w-3.5 text-primary-500" strokeWidth={2} />
              Responsive canvas
            </div>
            <p className="mt-0.5 text-[10px] leading-snug text-gray-500">
              Fills the full width of any screen. Overrides the device size.
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={isResponsiveCanvas}
            aria-label="Responsive canvas"
            onClick={() => handleResponsiveToggle(!isResponsiveCanvas)}
            disabled={typeof handleDeviceModeChange !== 'function'}
            className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-300 disabled:cursor-not-allowed disabled:opacity-50 ${
              isResponsiveCanvas ? 'bg-primary-600' : 'bg-gray-300 dark:bg-gray-600'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-200 ${
                isResponsiveCanvas ? 'translate-x-4' : 'translate-x-0.5'
              }`}
            />
          </button>
        </div>

        {/* Fixed device-size picker — hidden while the responsive canvas is on */}
        {isResponsiveCanvas ? (
          <p className="mt-3 rounded-lg bg-primary-50 px-3 py-2 text-[11px] leading-snug text-primary-800">
            The canvas adapts to each device's width. Switch this off to design for a fixed device size.
          </p>
        ) : (
          <div className="mt-3">
            {devicePresetGroups.length > 0 && typeof handleDeviceModeChange === 'function' ? (
              <GroupedListboxSelect
                label="Canvas size"
                hint="Preview dimensions for the builder canvas, grids, and layout templates."
                value={deviceMode}
                onChange={handleDeviceModeChange}
                groups={devicePresetGroups}
                placeholder="Choose a device preset…"
              />
            ) : (
              <p className="text-xs text-gray-500">No device presets are available for this study.</p>
            )}
          </div>
        )}
      </div>

      {/* ── Form Formatting Section ── always visible ── (FECOA-732) */}
      {typeof onGlobalFormattingChange === 'function' && (
        <FormFormattingSection
          globalFormatting={globalFormatting}
          onGlobalFormattingChange={onGlobalFormattingChange}
        />
      )}

      {/* ── Assessment Templates Section ── always visible ── */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 flex-shrink-0">
        <div className="flex items-center gap-2 mb-3">
          <div className="p-1.5 bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 rounded-lg">
            <Settings className="w-4 h-4" />
          </div>
          <div>
            <h3 className="text-xs font-bold text-gray-800 uppercase tracking-wide">Assessment Templates</h3>
            <p className="text-[10px] text-gray-500">Question layout templates for this assessment</p>
          </div>
        </div>
        <button
          onClick={() => setShowTemplateManager(true)}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded-lg transition-all shadow-sm hover:shadow-md"
        >
          <Settings className="w-4 h-4" />
          Manage Assessment Templates
        </button>
      </div>

      {/* ── Screen Button Layout Section ── visible when a button element is selected ── */}
      {selectedElementData?.type === 'button' && <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
        <div className="flex items-center gap-2 mb-4">
          <div className="p-1.5 bg-primary-100 text-primary-600 rounded-lg">
            <Layout className="w-4 h-4" />
          </div>
          <div>
            <h3 className="text-xs font-bold text-gray-800 uppercase tracking-wide">Screen Button Layout</h3>
            <p className="text-[10px] text-gray-500">Apply consistent navigation to all screens</p>
          </div>
        </div>
        <div className="space-y-4">
          {/* Load Template Dropdown */}
          <div className="bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-900 rounded-xl p-4 border border-gray-200 dark:border-gray-700 shadow-sm dark:shadow-gray-900/20 space-y-3">
            <div className="flex items-center space-x-2 mb-2">
              <div className="p-1.5 bg-blue-100 text-blue-600 rounded-lg">
                <Copy className="w-3.5 h-3.5" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Apply a Button Template</label>
                <p className="text-[9px] text-gray-400 mt-0.5">Copy a saved button layout onto this screen</p>
              </div>
            </div>
            <select
              value={selectedButtonTemplate}
              onChange={handleLoadButtonTemplate}
              className="w-full px-3 py-2.5 text-sm bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-lg focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
            >
              <option value="">Choose a button template...</option>
              {templates.filter(t => t.level === 'user').length > 0 && (
                <optgroup label="Your Personal Templates">
                  {templates.filter(t => t.level === 'user').map(t => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </optgroup>
              )}
              {templates.filter(t => t.level === 'assessment').length > 0 && (
                <optgroup label="This Assessment">
                  {templates.filter(t => t.level === 'assessment').map(t => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </optgroup>
              )}
              {templates.filter(t => t.level === 'sponsor').length > 0 && (
                <optgroup label="Sponsor Templates">
                  {templates.filter(t => t.level === 'sponsor').map(t => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </optgroup>
              )}
              {templates.filter(t => t.level === 'system').length > 0 && (
                <optgroup label="System Templates">
                  {templates.filter(t => t.level === 'system').map(t => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </optgroup>
              )}
            </select>
            {selectedButtonTemplate && (
              <div className="space-y-2">
                <button
                  onClick={handleApplyTemplateToAllScreens}
                  className="w-full px-3 py-2 bg-gradient-to-r from-primary-500 to-primary-600 text-white text-sm rounded-lg hover:from-primary-600 hover:to-primary-700 transition-all shadow-sm"
                >
                  Apply to All {safeScreens.length} Screens
                </button>
                {isSelectedBtnTemplateDeletable && (
                  <button
                    onClick={() => {
                      setDeleteConfirm({ id: selectedBtnTemplateObj.id, name: selectedBtnTemplateObj.name, type: 'button' });
                      setDeleteTypedName('');
                    }}
                    className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-all"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    Delete This Template
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Save Current Layout as Template */}
          <div className="bg-gradient-to-br from-white to-purple-50/30 dark:from-gray-800 dark:to-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700 shadow-sm dark:shadow-gray-900/20 space-y-3">
            <div className="flex items-center space-x-2 mb-2">
              <div className="p-1.5 bg-purple-100 text-purple-600 rounded-lg">
                <Save className="w-3.5 h-3.5" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Save Button Layout</label>
                <p className="text-[9px] text-gray-400 mt-0.5">Capture current button positions as a reusable template</p>
              </div>
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                placeholder='Template name (e.g., "Standard Navigation")'
                value={newTemplateName}
                onChange={(e) => setNewTemplateName(e.target.value)}
                className="flex-1 px-3 py-2.5 text-sm bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-lg focus:border-purple-400 focus:ring-2 focus:ring-purple-100 transition-all"
              />
              <button
                onClick={handleSaveButtonTemplate}
                disabled={!newTemplateName.trim()}
                className="px-4 py-2.5 bg-gradient-to-r from-purple-500 to-purple-600 text-white text-sm rounded-xl hover:from-purple-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm font-medium"
              >
                Save
              </button>
            </div>
            <div className="space-y-2">
              <label className="block text-[10px] font-medium text-gray-600 uppercase tracking-wide">Save to:</label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setTemplateSaveLevel('user')}
                  className={`px-3 py-2 text-xs rounded-lg border-2 transition-all ${
                    templateSaveLevel === 'user'
                      ? 'border-purple-500 bg-purple-50 text-purple-700 font-medium'
                      : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-600 hover:border-gray-300'
                  }`}
                >
                  My Library
                  <span className="block text-[9px] opacity-75 mt-0.5">Reuse across all assessments</span>
                </button>
                <button
                  type="button"
                  onClick={() => setTemplateSaveLevel('assessment')}
                  className={`px-3 py-2 text-xs rounded-lg border-2 transition-all ${
                    templateSaveLevel === 'assessment'
                      ? 'border-purple-500 bg-purple-50 text-purple-700 font-medium'
                      : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                  }`}
                >
                  This Assessment
                  <span className="block text-[9px] opacity-75 mt-0.5">Private to this assessment</span>
                </button>
              </div>
            </div>
            <label className="flex items-center text-xs text-gray-600 cursor-pointer p-2 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
              <input
                type="checkbox"
                checked={setTemplateAsDefault}
                onChange={(e) => setSetTemplateAsDefault(e.target.checked)}
                className="mr-2.5 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
              />
              Set as default button layout for new screens in this assessment
            </label>
          </div>

          {/* Help Text */}
          <div className="text-xs text-gray-500 bg-blue-50 p-3 rounded border border-blue-200">
            <strong>Button layouts are separate from Assessment Templates.</strong> Position your Back/Next buttons, save as a <em>button template</em>, then apply it across all screens. Save to <strong>My Library</strong> to reuse the same layout in future assessments.
          </div>
        </div>
      </div>}

      {/* ── Element Layout Controls ── only when the selected element has layout content ── */}
      {hasTemplatableResponseUI && (
        <div className="p-4 border-t-2 border-dashed border-primary-100">
          <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-200 dark:border-gray-700">
            <div className="p-1.5 bg-primary-100 text-primary-600 rounded-lg">
              <Move className="w-4 h-4" />
            </div>
            <div>
              <h3 className="text-xs font-bold text-gray-800 uppercase tracking-wide">Element Layout</h3>
              <p className="text-[10px] text-gray-500">
                {selectedElementData.type === 'question' ? 'Templates & alignment' : 'Layout templates'}
              </p>
            </div>
          </div>
          <div className="space-y-5">
            {/* Question Layout Template (for questions only) */}
            {hasTemplatableResponseUI && (
              <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700 shadow-sm dark:shadow-gray-900/20">
                <div className="flex items-center space-x-2 mb-3">
                  <div className="p-1.5 bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 rounded-lg">
                    <Layers className="w-3.5 h-3.5" />
                  </div>
                  <div>
                    <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Apply Layout Template</h4>
                    <p className="text-[9px] text-gray-400 mt-0.5">Apply a saved layout to this and similar questions</p>
                  </div>
                </div>
                {loadingTemplates ? (
                  <div className="flex items-center justify-center py-3 text-gray-400">
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    <span className="text-xs">Loading templates...</span>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <select
                      value={selectedQuestionTemplate}
                      onChange={(e) => {
                        const templateId = e.target.value;
                        setSelectedQuestionTemplate(templateId);
                        if (templateId) {
                          const allTemplates = [
                            ...questionTemplates.system,
                            ...questionTemplates.sponsor,
                            ...questionTemplates.user,
                            ...questionTemplates.assessment,
                            ...questionTemplates.other.map(o => o.template)
                          ];
                          const template = allTemplates.find(t => String(t.id) === templateId);
                          if (template) applyQuestionTemplate(template);
                        }
                      }}
                      className="w-full px-3 py-2 text-xs bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-lg focus:border-primary-400 focus:ring-2 focus:ring-primary-100 dark:focus:ring-primary-900/30 transition-all"
                    >
                      {(() => {
                        const filterByDevice = (templates) => templates.filter(t =>
                          matchesTemplateDevice(t, currentDeviceCategory)
                        );
                        const filteredSystem = filterByDevice(questionTemplates.system);
                        const filteredSponsor = filterByDevice(questionTemplates.sponsor);
                        const filteredUser = filterByDevice(questionTemplates.user);
                        const filteredAssessment = filterByDevice(questionTemplates.assessment);
                        const filteredOther = questionTemplates.other.filter(o =>
                          matchesTemplateDevice(o.template, currentDeviceCategory)
                        );

                        // Disambiguate duplicate names in user templates
                        const getUniqueTemplateName = (template, index, templates) => {
                          const duplicates = templates.filter(t => t.name === template.name);
                          if (duplicates.length <= 1) return template.name;
                          
                          // If there are duplicates, add a suffix
                          const duplicateIndex = duplicates.findIndex(t => t.id === template.id);
                          if (duplicateIndex === 0) {
                            // First duplicate: add date if available
                            if (template.created_at) {
                              const date = new Date(template.created_at);
                              const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                              return `${template.name} (${dateStr})`;
                            }
                          }
                          // For other duplicates or no date: add numeric suffix
                          return `${template.name} (${duplicateIndex + 1})`;
                        };
                        return (
                          <>
                            <option value="">Select a template...</option>
                            {filteredUser.length > 0 && (
                              <optgroup label="Your Templates">
                                {filteredUser.map((t, index) => (
                                  <option key={t.id} value={t.id}>{getUniqueTemplateName(t, index, filteredUser)}</option>
                                ))}
                              </optgroup>
                            )}
                            {filteredAssessment.length > 0 && (
                              <optgroup label="This Assessment">
                                {filteredAssessment.map((t, index) => (
                                  <option key={t.id} value={t.id}>{getUniqueTemplateName(t, index, filteredAssessment)}</option>
                                ))}
                              </optgroup>
                            )}
                            {filteredSystem.length > 0 && (
                              <optgroup label="System Templates">
                                {filteredSystem.map(t => (
                                  <option key={t.id} value={t.id}>{t.name}</option>
                                ))}
                              </optgroup>
                            )}
                            {filteredSponsor.length > 0 && (
                              <optgroup label="Sponsor Templates">
                                {filteredSponsor.map(t => (
                                  <option key={t.id} value={t.id}>{t.name}</option>
                                ))}
                              </optgroup>
                            )}
                            {filteredOther.length > 0 && (
                              <optgroup label="From Other Assessments">
                                {filteredOther.map(o => (
                                  <option key={o.template.id} value={o.template.id}>
                                    {o.assessment_name}: {o.template.name}
                                  </option>
                                ))}
                              </optgroup>
                            )}
                          </>
                        );
                      })()}
                    </select>
                    {selectedQuestionTemplate && selectedQTemplateObj && (
                      <div className="space-y-2">
                        {similarCount > 0 && (
                          <button
                            onClick={applyTemplateToSimilarElements}
                            className="w-full px-3 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm rounded-lg transition-all shadow-sm"
                          >
                            Apply to All {similarCount + 1} {displayType(selectedElementData.data?.type) || 'Similar'} Elements
                          </button>
                        )}
                        {similarCount === 0 && (
                          <button
                            onClick={applyTemplateToSimilarElements}
                            className="w-full px-3 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm rounded-lg transition-all shadow-sm"
                          >
                            Set as Default for {displayType(selectedElementData.data?.type) || 'This Type'}
                          </button>
                        )}
                        <p className="text-[9px] text-gray-400 text-center">
                          {similarCount > 0
                            ? `Applies to this element + ${similarCount} other ${displayType(selectedElementData.data?.type)} question${similarCount > 1 ? 's' : ''} and sets as default`
                            : 'New questions of this type will use this template automatically'}
                        </p>
                        {isSelectedQTemplateDeletable && (
                          <button
                            onClick={() => {
                              setDeleteConfirm({ id: selectedQTemplateObj.id, name: selectedQTemplateObj.name, type: 'question' });
                              setDeleteTypedName('');
                            }}
                            className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-all"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                            Delete This Template
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Save as Question Layout Template (for questions only) */}
            {hasTemplatableResponseUI && (
              <SaveQuestionTemplateSection
                selectedElementData={selectedElementData}
                assessmentId={assessmentId}
                deviceMode={deviceMode}
                onTemplateSaved={async () => {
                  // A newly saved template must also reach the mount-time preload
                  // cache backing the Manage Assessment Templates modal; without
                  // this the first saved template never shows there until a page
                  // reload (FECOA-1844).
                  if (refreshTemplatesCache) refreshTemplatesCache();
                  const questionType = getEffectiveQuestionType(selectedElementData);
                  setLoadingTemplates(true);
                  try {
                    const response = await api.get('/api/assessment-builder/layout-templates/available/', {
                      params: { question_type: questionType, assessment_id: assessmentId }
                    });
                    setQuestionTemplates({
                      system: response.data.system_templates || [],
                      sponsor: response.data.sponsor_templates || [],
                      user: response.data.user_templates || [],
                      assessment: response.data.assessment_templates || [],
                      other: response.data.assessment_templates_from_other_assessments || []
                    });
                  } catch (error) {
                    console.error('Failed to refresh templates:', error);
                  } finally {
                    setLoadingTemplates(false);
                  }
                }}
              />
            )}

          </div>
        </div>
      )}

      <TemplateManagerModal
        isOpen={showTemplateManager}
        onClose={() => setShowTemplateManager(false)}
        assessmentId={assessmentId}
        preloadedTemplates={allTemplatesCache}
        appliedDefaultTemplateIds={appliedDefaultTemplateIds}
        selectedElementData={selectedElementData}
        applyQuestionTemplate={applyQuestionTemplate}
        applyTemplateToAllOfType={applyTemplateToAllOfType}
        screens={screens}
        currentScreenIndex={currentScreenIndex}
        deviceMode={deviceMode}
        onTemplateUpdated={() => {
          // Refresh the parent cache so the modal picks up applied defaults
          if (refreshTemplatesCache) refreshTemplatesCache();
          if (isTemplatableElement(selectedElementData)) {
            const fetchQuestionTemplates = async () => {
              const questionType = getEffectiveQuestionType(selectedElementData);
              setLoadingTemplates(true);
              try {
                const response = await api.get('/api/assessment-builder/layout-templates/available/', {
                  params: {
                    question_type: questionType,
                    assessment_id: assessmentId
                  }
                });
                setQuestionTemplates({
                  system: response.data.system_templates || [],
                  sponsor: response.data.sponsor_templates || [],
                  user: response.data.user_templates || [],
                  assessment: response.data.assessment_templates || [],
                  other: response.data.assessment_templates_from_other_assessments || []
                });
              } catch (error) {
                console.error('Failed to fetch question templates:', error);
              } finally {
                setLoadingTemplates(false);
              }
            };
            fetchQuestionTemplates();
          }
        }}
      />

      {/* Delete Confirmation Modal (shared for button + question templates) */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-60 dark:bg-opacity-70 flex items-center justify-center z-[10000]">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl dark:shadow-gray-900/20 w-full max-w-md p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900">Delete Template</h3>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              This action cannot be undone. To confirm, type the template name below:
            </p>
            <div className="bg-gray-50 dark:bg-gray-900 rounded-lg px-3 py-2 mb-3 border border-gray-200 dark:border-gray-700">
              <span className="text-sm font-mono font-semibold text-gray-800">{deleteConfirm.name}</span>
            </div>
            <input
              type="text"
              placeholder="Type template name to confirm..."
              value={deleteTypedName}
              onChange={(e) => setDeleteTypedName(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 mb-4"
              autoFocus
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => { setDeleteConfirm(null); setDeleteTypedName(''); }}
                className="px-4 py-2 text-sm bg-gray-100 dark:bg-gray-800 text-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (deleteConfirm.type === 'question') handleDeleteQuestionTemplate();
                  else if (deleteConfirm.type === 'button') handleDeleteButtonTemplateConfirmed();
                }}
                disabled={deleteTypedName !== deleteConfirm.name}
                className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Delete Template
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LayoutPanel;